import React, { useEffect, useRef } from 'react';
import { TURNSTILE_SITE_KEY, setGlobalTurnstileToken } from '../hooks/useTurnstile';

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: TurnstileOptions) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

interface TurnstileOptions {
  sitekey: string;
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact' | 'invisible';
}

interface TurnstileWidgetProps {
  onSuccess?: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
}

/**
 * Invisible Cloudflare Turnstile widget for bot protection.
 * This component loads the Turnstile script and renders an invisible challenge.
 */
const TurnstileWidget: React.FC<TurnstileWidgetProps> = ({
  onSuccess,
  onExpire,
  onError
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    // Load Turnstile script if not already loaded
    const loadScript = () => {
      if (document.querySelector('script[src*="turnstile"]')) {
        scriptLoadedRef.current = true;
        renderWidget();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
      script.async = true;
      script.defer = true;

      window.onTurnstileLoad = () => {
        scriptLoadedRef.current = true;
        renderWidget();
      };

      document.head.appendChild(script);
    };

    const renderWidget = () => {
      if (!window.turnstile || !containerRef.current) return;
      
      // Remove existing widget if any
      if (widgetIdRef.current) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          // Ignore removal errors
        }
      }

      // Render new widget
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => {
          console.log('✅ Turnstile: Token acquired successfully');
          setGlobalTurnstileToken(token);
          onSuccess?.(token);
        },
        'expired-callback': () => {
          console.warn('⚠️ Turnstile: Token expired, will refresh');
          setGlobalTurnstileToken(null);
          onExpire?.();
        },
        'error-callback': () => {
          console.error('❌ Turnstile: Verification failed - browser tracking protection may be blocking');
          setGlobalTurnstileToken(null);
          onError?.();
        },
        theme: 'auto',
        size: 'invisible'
      });
    };

    // Check if script already loaded
    if (window.turnstile) {
      scriptLoadedRef.current = true;
      renderWidget();
    } else {
      loadScript();
    }

    // Cleanup
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          // Ignore removal errors
        }
      }
    };
  }, [onSuccess, onExpire, onError]);

  // Invisible - no visible UI
  return <div ref={containerRef} style={{ display: 'none' }} />;
};

export default TurnstileWidget;
